import type { LucideIcon } from 'lucide-react';
import {
  LayoutDashboard,
  UserPlus,
  Users,
  ClipboardCheck,
  CalendarRange,
  Repeat,
  UserCheck,
  Users2,
  Banknote,
  Mail,
  BarChart2,
  LifeBuoy,
  ShieldAlert,
} from 'lucide-react';

/**
 * Staff Operations Guide content — the in-app version of the Education Director
 * Operations Manual. Grounded in verified workflows; the "Where" values match
 * the real sidebar labels. Each section is also surfaced contextually on its
 * page via the global route-aware help (see src/components/GlobalHelp.tsx).
 */

export type Block =
  | { kind: 'p'; text: string }
  | { kind: 'steps'; items: string[] }
  | { kind: 'where'; text: string }
  | { kind: 'link'; label: string; href: string }
  | { kind: 'callout'; tone: 'info' | 'warn' | 'success'; title?: string; body: string }
  | { kind: 'pitfalls'; items: { problem: string; fix: string }[] }
  | { kind: 'image'; src: string; alt: string; caption?: string };

export interface Section {
  id: string;
  icon: LucideIcon;
  tag: string;
  title: string;
  tldr: string;
  blocks: Block[];
}

export const SUPPORT_EMAIL = 'education@centralfloridavocalarts.org';

export const OPS_SECTIONS: Section[] = [
  {
    id: 'dashboard',
    icon: LayoutDashboard,
    tag: 'Daily',
    title: 'Your daily rhythm',
    tldr: 'Start every day on the Dashboard — it surfaces exactly what needs you.',
    blocks: [
      { kind: 'where', text: 'Home → Dashboard' },
      {
        kind: 'p',
        text: "Open the Dashboard each morning. It shows today's lessons and who's checked in, open makeup credits, flagged exceptions (no-shows / missing check-ins), and recent email activity. Work top to bottom, then handle enrollment, scheduling changes, and end-of-period billing as they come up.",
      },
      {
        kind: 'callout',
        tone: 'info',
        title: 'Help is on every page',
        body: 'A floating "Help" button (bottom-right) opens this guide for whatever screen you\'re on. On the busiest pages — Schedule, Teacher Check-In, Prospects, and Families — a "Tour" button runs a quick guided walkthrough that points right at the buttons you need.',
      },
      { kind: 'link', label: 'Open the Dashboard', href: '/admin-operations-dashboard' },
    ],
  },
  {
    id: 'enroll',
    icon: UserPlus,
    tag: 'Enrollment',
    title: 'Enroll a new student',
    tldr: 'Inquiry → intro lesson → ongoing lessons. The system does the work; you approve twice.',
    blocks: [
      { kind: 'where', text: 'People → Prospects' },
      {
        kind: 'p',
        text: 'New families flow through the Prospects pipeline. You review and approve at two points; everything else is automatic.',
      },
      {
        kind: 'image',
        src: '/guide/prospects.png',
        alt: 'The Prospect Pipeline page showing the student status flow (Prospect → Intro Booked → Intro Complete → Active) and a list of inquiries.',
        caption:
          'The Prospect Pipeline. Click an inquiry to expand it and reveal the "Confirm Prospect" / "Approve" buttons. (Demo data.)',
      },
      { kind: 'p', text: 'From inquiry to a booked intro:' },
      {
        kind: 'steps',
        items: [
          'A new inquiry appears with status New. Review the details (student, instrument, preferred day/time, parent email).',
          'Click "Confirm Prospect" → this creates the student + parent contact and emails the family a secure booking link (inquiry status moves New → Confirmed → Invite Sent).',
          'The family books an intro time — it appears under Scheduling requests as Pending.',
          'Click "Approve Request" (it reads "Approve & Send Payment Email" once the link is ready) → the family gets the $40 intro payment link (request status → Approved → Payment Sent).',
          'When they pay, it all happens automatically: the intro is scheduled and a family login is created (request status → Paid; the inquiry becomes Converted).',
        ],
      },
      {
        kind: 'callout',
        tone: 'info',
        title: 'No fee on the intro',
        body: 'The intro is $40 only — no registration fee. After the intro is taught and checked in as Attended, the family can book ongoing lessons (monthly tuition + a one-time $50 registration fee on first enrollment).',
      },
      { kind: 'link', label: 'Open Prospects', href: '/prospects' },
    ],
  },
  {
    id: 'families',
    icon: Users,
    tag: 'Setup',
    title: 'The Families page — billing, catch-up, onboarding',
    tldr: 'Your home base for getting families onto our system. Three one-click actions per family.',
    blocks: [
      { kind: 'where', text: 'Home → Families' },
      {
        kind: 'image',
        src: '/guide/families.png',
        alt: 'The Families page showing a family card with status dots and the Pay link, Catch-up, and Onboarding buttons.',
        caption: 'Each family shows its status and its three actions. (Demo data.)',
      },
      {
        kind: 'p',
        text: 'Opens on "Needs attention." Each family shows three status dots — Account (do they have a login?), Billing (are they on our billing?), Schedule (are lessons booked out?) — and its next step in plain English.',
      },
      {
        kind: 'steps',
        items: [
          'Find the family (search or filter pills).',
          'Pay link — starts their monthly tuition at the correct rate (no registration fee for existing families).',
          'Catch-up — opens a small window ("Catch-up charge — [student]"): type the Amount (USD) and an optional note the family sees, then click "Generate catch-up link". If the family is on a monthly plan, quick-pick buttons for 1/2/3 months of their tuition appear above the amount. Use it for a one-time balance owed.',
          'Onboarding — sends an account-setup link so they can log in.',
          'Each button gives you a link to Copy or a drafted email to Send — your choice.',
        ],
      },
      {
        kind: 'image',
        src: '/guide/catchup.png',
        alt: 'The Catch-up charge window: an Amount (USD) field and an optional note shown to the family, with a "Generate catch-up link" button.',
        caption:
          'The Catch-up window. The 1/2/3-month presets appear only when the family is on a monthly plan. (Demo data.)',
      },
      {
        kind: 'callout',
        tone: 'success',
        title: 'Safe by design',
        body: "Nothing is charged and nothing is sent unless you send it. Families aren't billed until they open the link and pay — and when they do, the status dots turn green on their own.",
      },
      {
        kind: 'callout',
        tone: 'info',
        title: 'Rule of thumb',
        body: 'Account dot not green → send Onboarding. Billing not green → send Pay link.',
      },
      { kind: 'link', label: 'Open Families', href: '/families' },
    ],
  },
  {
    id: 'attendance',
    icon: ClipboardCheck,
    tag: 'Lessons',
    title: 'Attendance & check-ins',
    tldr: 'Five attendance statuses — and what each one triggers for pay, billing, and makeups.',
    blocks: [
      {
        kind: 'where',
        text: 'Scheduling → Teacher Check-In (or open a lesson on the Schedule Calendar)',
      },
      {
        kind: 'p',
        text: 'Teachers check in their own lessons; you can review and set attendance here. On Teacher Check-In you pick one of five options (shown as radio buttons):',
      },
      {
        kind: 'image',
        src: '/guide/attendance.png',
        alt: 'The Submit Check-In modal with the five attendance status options.',
        caption: 'Click "Check in" on a lesson to open this. (Demo data.)',
      },
      {
        kind: 'steps',
        items: [
          'Attended — the lesson happened. Pays the teacher; counts for billing.',
          "No-Show — student didn't come. Pays the teacher and counts for billing; no makeup credit (the miss is on the student).",
          'Cancelled — Student (Late) — cancelled inside 24 hours. Same as No-Show: pays + bills, no credit.',
          'Cancelled — Student (24hr Notice) — cancelled with notice. Does NOT pay/bill; auto-issues a makeup credit.',
          'Cancelled — Teacher — the teacher cancelled. Does NOT pay/bill; auto-issues a makeup credit.',
        ],
      },
      {
        kind: 'p',
        text: 'Below the status, the modal has a few optional fields: a "Supplementary materials sent" toggle (flip it on and paste a link — note that sending materials means no makeup credit is created), a staff-only note, and "Session Progress Notes" the family sees in their portal. When you\'re done, click "Review Submission", check the summary, then "Confirm & Submit" to save.',
      },
      {
        kind: 'p',
        text: 'Editing a lesson directly on the Schedule Calendar also exposes "Pending" (not yet marked) and "Holiday - Cancelled" (closure day).',
      },
      {
        kind: 'callout',
        tone: 'warn',
        title: 'What pays / what bills (current setting)',
        body: 'Attended, No-Show, and late (<24hr) cancels pay the teacher and count toward venue billing. "24hr notice" and "Teacher" cancels do neither — instead they auto-issue the family a makeup credit. (What pays is configurable under Settings → Payroll.)',
      },
      {
        kind: 'callout',
        tone: 'info',
        title: 'Makeup credits are automatic — but only for two statuses',
        body: 'Setting a lesson to "24hr notice" or "Teacher" cancel automatically creates a makeup credit (for individual lessons, unless make-up material was already sent). No-Show and late cancels do NOT create a credit.',
      },
      {
        kind: 'pitfalls',
        items: [
          {
            problem: 'I marked a 24hr-notice/teacher cancel AND issued a credit by hand.',
            fix: 'The credit was already auto-created — void the duplicate under Finance → Credits.',
          },
        ],
      },
      { kind: 'link', label: 'Open Teacher Check-In', href: '/teacher-check-in-dashboard' },
    ],
  },
  {
    id: 'scheduling',
    icon: CalendarRange,
    tag: 'Scheduling',
    title: 'Scheduling',
    tldr: 'The studio-wide calendar: create, edit, reschedule, plus holidays, exceptions, and availability.',
    blocks: [
      { kind: 'where', text: 'Scheduling → Schedule Calendar' },
      {
        kind: 'image',
        src: '/guide/schedule-lesson.png',
        alt: 'The Schedule a Lesson modal — teacher, student, date/time, recurrence, and the Schedule Lesson button.',
        caption: 'The "Schedule" button opens this form. (Demo data.)',
      },
      {
        kind: 'steps',
        items: [
          'Add a lesson: click the "Schedule" button (top-right) to open the "Schedule a Lesson" window. Set the Date, Start, and End time; pick the Teacher, the Student (or tick "Group lesson" — see Group classes), the Program, the Lesson Type (Standard / Intro / Group / Virtual / Makeup), and the Location. To repeat it, open the "Recurrence" dropdown ("Does not repeat", "Weekly", "Every 2 weeks", "Monthly") and set how it ends. Click "Schedule Lesson" to save.',
          'Edit a lesson: open it and change any field. For a repeating series, the "Apply to all future lessons" checkbox OFF changes just this lesson; ON changes all future ones.',
          'Delete a repeating lesson: choose this one, this + future, or the whole series.',
        ],
      },
      {
        kind: 'callout',
        tone: 'info',
        title: 'Rescheduling one lesson',
        body: 'Families cannot self-reschedule — they email the studio and you move the lesson. If a teacher requests a reschedule, it comes to you to confirm; nothing moves until you do.',
      },
      {
        kind: 'p',
        text: 'Related: Holiday Calendar (block closed days), Exceptions (resolve flagged lessons), Availability (approve teacher availability so times become bookable).',
      },
      {
        kind: 'pitfalls',
        items: [
          {
            problem: 'I changed a whole series by accident.',
            fix: 'Edit again — test on one lesson first before using "apply to future."',
          },
        ],
      },
      { kind: 'link', label: 'Open Schedule Calendar', href: '/schedule-calendar' },
    ],
  },
  {
    id: 'group-classes',
    icon: Users2,
    tag: 'Scheduling',
    title: 'Group classes',
    tldr: 'Schedule a recurring group class and list who can cover it; a teacher claims it and checks it in.',
    blocks: [
      {
        kind: 'where',
        text: 'Scheduling → Schedule Calendar (you) · Teacher Portal (the teacher claims it)',
      },
      {
        kind: 'image',
        src: '/guide/schedule-group.png',
        alt: 'The Schedule a Lesson window with "Group lesson" checked — the student picker is replaced by a "Co-Teachers (who can claim this class)" list.',
        caption: 'Tick "Group lesson" and the Co-Teachers list appears. (Demo data.)',
      },
      {
        kind: 'steps',
        items: [
          'On the Schedule Calendar, click the "Schedule" button (top-right of the page). The "Schedule a Lesson" window opens.',
          'Set the Date, Start time, and End time at the top of the window.',
          'Tick the "Group lesson" checkbox — it sits to the right of the "Student" label. The single-student picker disappears and a "Co-Teachers (who can claim this class)" list appears.',
          "Pick the primary Teacher in the Teacher dropdown; then in the Co-Teachers list, check everyone else who's allowed to cover it. The primary and any co-teacher can later claim the session for the group rate.",
          'Open the "Recurrence" dropdown and choose Weekly / Every 2 weeks / Monthly (group classes are almost always recurring). Choose the Program and Location, then click "Schedule Lesson" to save it.',
          'The class now sits on the calendar unclaimed. A teacher claims it from their Teacher Portal — the claim button flips to "Claimed by you", which assigns that session\'s pay to them.',
          "After they've taught it, the teacher checks it in from Teacher Check-In — that check-in confirms they held the class (it's recorded with a GROUP badge).",
        ],
      },
      {
        kind: 'callout',
        tone: 'info',
        body: "Group classes aren't tied to individual students — no per-student roster, and they never create makeup credits. Pay is a group rate to whoever claims the session.",
      },
      {
        kind: 'pitfalls',
        items: [
          {
            problem: 'A group class has no one lined up to teach it.',
            fix: "It's unclaimed — the Primary teacher or a Co-Teacher needs to claim it from their Teacher Portal. Add more Co-Teachers if coverage is thin.",
          },
        ],
      },
      { kind: 'link', label: 'Open Schedule Calendar', href: '/schedule-calendar' },
    ],
  },
  {
    id: 'makeups',
    icon: Repeat,
    tag: 'Makeups',
    title: 'Makeups & credits',
    tldr: 'A 24hr-notice or teacher cancel auto-issues a credit; families book their own makeups.',
    blocks: [
      {
        kind: 'image',
        src: '/guide/makeup.png',
        alt: 'The Makeup Scheduling page: a list of open credits on the left; selecting one shows available teacher slots to book.',
        caption:
          'Makeup Scheduling — pick an open credit on the left to see bookable slots, then "Confirm Booking". (Demo data.)',
      },
      {
        kind: 'where',
        text: 'Finance → Credits (view / void) · Scheduling → Makeup Scheduling (book)',
      },
      {
        kind: 'steps',
        items: [
          'Credits are created automatically when a lesson is set to "Cancelled — Student (24hr Notice)" or "Cancelled — Teacher". No-Show and late cancels do NOT create one.',
          'Families book their own makeups from their portal (Billing tab). You can also book one for them under Makeup Scheduling — pick a slot and hit "Confirm Booking".',
          'The Credits page is for viewing and voiding (the "Void Credit" button) — credits aren\'t issued by hand there. Lifecycle: Open → Scheduled → Used (unused ones expire).',
        ],
      },
      { kind: 'link', label: 'Open Makeup Scheduling', href: '/makeup-scheduling' },
    ],
  },
  {
    id: 'substitutions',
    icon: UserCheck,
    tag: 'Coverage',
    title: 'Substitutions',
    tldr: "Reassign a lesson when a teacher can't make it — families and subs are notified automatically.",
    blocks: [
      { kind: 'where', text: 'Scheduling → Substitutions (or open the lesson on the calendar)' },
      {
        kind: 'steps',
        items: [
          'Start a substitution on the affected lesson, give a reason, pick the substitute teacher, and click "Assign & Notify".',
          "The system moves the lesson to the sub, updates both teachers' calendars, and automatically notifies the family and the sub.",
        ],
      },
      {
        kind: 'callout',
        tone: 'info',
        body: 'Teachers can also request a sub from their portal — those requests come to you to assign.',
      },
      { kind: 'link', label: 'Open Substitutions', href: '/substitutions' },
    ],
  },
  {
    id: 'billing',
    icon: Banknote,
    tag: 'Finance',
    title: 'Billing & payroll',
    tldr: 'Families pay via Stripe; teachers are paid via QuickBooks; venues are billed by the hour.',
    blocks: [
      {
        kind: 'p',
        text: "Families pay us through Stripe (subscriptions + one-time links). We pay teachers through QuickBooks — the app tracks what's earned; it doesn't move teacher money.",
      },
      { kind: 'where', text: 'Finance → Teacher Pay Ledger · Venues · Payment Dunning' },
      {
        kind: 'steps',
        items: [
          'Teacher Pay Ledger — pick a biweekly pay period (Sun–Sat) to see each teacher\'s lessons and earnings. It\'s read-only; use "Export for QuickBooks" and pay there.',
          'Venues → Usage — hours and $ owed per venue for any date range. Payouts — "Create batch" for a venue\'s outstanding charges, then "Mark paid" (it asks for a method + reference).',
          'Payment Dunning — follow up on failed/late family payments.',
        ],
      },
      {
        kind: 'callout',
        tone: 'warn',
        title: 'Payroll toggles are global',
        body: 'Which lesson outcomes pay a teacher is a setting (Settings → Payroll). Currently Attended, No-Show, and late (<24hr) cancels all pay. Changing those toggles affects every period, so change them deliberately.',
      },
      {
        kind: 'callout',
        tone: 'info',
        title: 'New teachers auto-sync to QuickBooks',
        body: 'When a teacher completes the W-9 step of onboarding, the app automatically creates them in QuickBooks as a 1099 vendor — with their Tax ID and signed W-9 already on file (attached to the vendor). You do NOT re-create the vendor or re-key the W-9. The only setup left is direct deposit: open the vendor in QuickBooks and either enter their bank info or click "Send email" to have them add it themselves. Bank details are the one thing that can\'t be automated (Intuit has no API for it).',
      },
      {
        kind: 'callout',
        tone: 'info',
        title: 'Venues we pay',
        body: 'College Park Presbyterian, Audubon Park Covenant, Christ Community — at $10/hour. Virtual lessons and school-based Arts 1:1 are not venue-billed.',
      },
      { kind: 'link', label: 'Open Teacher Pay Ledger', href: '/teacher-payouts' },
      { kind: 'link', label: 'Open Venue Usage', href: '/admin/venue-usage' },
    ],
  },
  {
    id: 'communications',
    icon: Mail,
    tag: 'Comms',
    title: 'Communications',
    tldr: 'Templates, quick replies, and a full log of everything sent.',
    blocks: [
      {
        kind: 'where',
        text: 'Communications → Email & SMS Templates · Quick Replies · Email Queue',
      },
      {
        kind: 'steps',
        items: [
          'Templates are your reusable messages (reminders, receipts, cancellations). Edit subject/body and hit "Save Changes"; placeholders like the student\'s name fill in automatically.',
          'Email Queue is the log of everything sent — check delivery status and hit "Retry" on anything that failed.',
        ],
      },
      {
        kind: 'callout',
        tone: 'info',
        body: 'What\'s automatic: 24-hour lesson reminders and payment receipts send on their own. Onboarding/setup emails go out when you trigger them from the Families page. There is no automatic post-lesson "recap" email — teachers leave lesson notes in the portal instead.',
      },
      { kind: 'link', label: 'Open Email Queue', href: '/email-queue' },
    ],
  },
  {
    id: 'reports',
    icon: BarChart2,
    tag: 'Insights',
    title: 'Reports & insights',
    tldr: 'Trends and detailed, exportable reports.',
    blocks: [
      { kind: 'where', text: 'Insights → Reports · Operational Metrics' },
      {
        kind: 'p',
        text: 'Reports has tabs — Overview, Programs, Credits, Attendance, Teachers, Exceptions, Lesson Revenue, Staff Performance, Compliance Audit, and more — each filterable with an "Export CSV" button. Operational Metrics shows trends over time; the Dashboard is your live daily snapshot.',
      },
      { kind: 'link', label: 'Open Reports', href: '/reports' },
    ],
  },
  {
    id: 'supporting-families',
    icon: LifeBuoy,
    tag: 'Support',
    title: 'Supporting families',
    tldr: 'What families experience — so you can help them fast.',
    blocks: [
      {
        kind: 'p',
        text: 'Families use a self-serve portal after they log in. Hand them the companion "Family & Student Guide." The short version:',
      },
      {
        kind: 'steps',
        items: [
          'They set a password from an invite link, then land on their portal (Upcoming, Lessons, Billing, Resources, Profile).',
          'They can join virtual lessons, see materials and notes, book makeups, update their card (Billing → Manage Subscription), and manage contacts/notifications.',
          'They CANNOT self-reschedule a single lesson (they email you); cancellation carries a 30-day notice.',
          'The app installs to a phone home screen (Add to Home Screen).',
        ],
      },
    ],
  },
  {
    id: 'golden-rules',
    icon: ShieldAlert,
    tag: 'Must-know',
    title: 'Golden rules & gotchas',
    tldr: 'The handful of things that prevent every common mistake.',
    blocks: [
      {
        kind: 'steps',
        items: [
          'No family is charged and no email is sent unless YOU send it — links and drafts are safe to generate and review first.',
          "A 24hr-notice or teacher cancel auto-issues a makeup credit (No-Show and late cancels do not) — don't double-issue.",
          '"Apply to future" changes an entire recurring series — test on one lesson first.',
          'Teacher pay-per-outcome toggles (Settings → Payroll) are global and affect past periods — change deliberately.',
          'Rescheduling a single lesson is staff-handled (families email you). Cancelling a plan IS family self-serve — it just carries a 30-day notice and emails you; there is no approval step.',
          'Teacher money moves in QuickBooks, not the app; the app only tracks earnings.',
          'Summer Institute is a separate system with its own in-app guides.',
        ],
      },
    ],
  },
];
